/**
 * PlatformBillingPage — Vista de billing para super_admin (DD-PLATFORM-01).
 *
 * 3 tabs: Suscripciones, Métodos de pago, Eventos webhook.
 * Tabla HTML simple con clases Tailwind tokens cumple-* — mismo patrón que
 * PlatformTenants y PlatformUsers (sin DataTable genérico por ahora; ver
 * TODO comentario en PlatformTenants.tsx).
 *
 * Sprint DD: solo lectura. Mutations quedan para Sprint EE.
 */
import { useEffect, useState } from 'react';

import { useAppShell } from '../components/AppShell.js';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { useAuth } from '../auth/AuthContext.js';
import { loadSession } from '../auth/storage.js';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

interface SubscriptionRow {
  id: string;
  tenantId: string;
  status: string;
  billingCycle: string;
  currentPeriodEnd: string;
  externalProvider: string | null;
  tenant: { id: string; name: string; slug: string };
  plan: { id: string; name: string; monthlyPriceUsd: string | null };
}

interface PaymentMethodRow {
  id: string;
  tenantId: string;
  gateway: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  status: string;
  tenant: { id: string; name: string; slug: string };
}

interface WebhookEventRow {
  id: string;
  eventType: string;
  externalEventId: string | null;
  paymentId: string | null;
  actorKind: string;
  createdAt: string;
  payment: { id: string; tenantId: string; provider: string } | null;
}

interface ListMeta {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
}

type ActiveTab = 'subscriptions' | 'payment-methods' | 'webhook-events';

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

function getToken(): string {
  // La sesión vive en sessionStorage bajo 'assessos.auth.session' (auth/storage).
  // Leerla por loadSession evita el bug de leer localStorage['session'] (token vacío
  // -> 401 missing_token). Fuente canónica única.
  return loadSession()?.token ?? '';
}

async function apiFetch(path: string): Promise<Response> {
  const token = getToken();
  return fetch(`/api/platform/billing${path}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export function PlatformBillingPage(): JSX.Element {
  const { setBackTo } = useAppShell();
  const { session } = useAuth();

  useEffect(() => {
    setBackTo(null);
    return () => setBackTo(null);
  }, [setBackTo]);

  const [activeTab, setActiveTab] = useState<ActiveTab>('subscriptions');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Billing — Vista global</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex gap-4" aria-label="Tabs de billing">
          {(
            [
              { key: 'subscriptions', label: 'Suscripciones' },
              { key: 'payment-methods', label: 'Métodos de pago' },
              { key: 'webhook-events', label: 'Eventos webhook' },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={[
                'whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                activeTab === key
                  ? 'border-cumple-primary-600 text-cumple-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenido del tab activo */}
      {activeTab === 'subscriptions' && <SubscriptionsTab token={session?.token} />}
      {activeTab === 'payment-methods' && <PaymentMethodsTab token={session?.token} />}
      {activeTab === 'webhook-events' && <WebhookEventsTab token={session?.token} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Suscripciones
// ─────────────────────────────────────────────────────────────────────────────

function SubscriptionsTab({ token }: { token: string | undefined }): JSX.Element {
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), pageSize: '25' });
    if (statusFilter) params.set('status', statusFilter);

    apiFetch(`/subscriptions?${params.toString()}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json() as Promise<{ data: SubscriptionRow[]; meta: ListMeta }>;
      })
      .then(({ data, meta: m }) => {
        setRows(data);
        setMeta(m);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Error cargando suscripciones'),
      )
      .finally(() => setLoading(false));
  }, [token, page, statusFilter]);

  if (loading) return <LoadingSkeleton rows={5} />;
  if (error) return <p className="text-red-600 text-sm">{error}</p>;
  if (!rows.length)
    return <EmptyState title="Sin suscripciones" description="No hay suscripciones activas." />;

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activa</option>
          <option value="past_due">Vencida</option>
          <option value="suspended">Suspendida</option>
          <option value="cancelled">Cancelada</option>
        </select>
        {meta && <span className="text-sm text-gray-500">{meta.total} suscripciones</span>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Tenant', 'Plan', 'Estado', 'Ciclo', 'Próx. cobro', 'Gateway'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{row.tenant.name}</td>
                <td className="px-4 py-3 text-gray-600">{row.plan.name}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-4 py-3 text-gray-600">{row.billingCycle}</td>
                <td className="px-4 py-3 text-gray-600">
                  {new Date(row.currentPeriodEnd).toLocaleDateString('es-DO')}
                </td>
                <td className="px-4 py-3 text-gray-500">{row.externalProvider ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta && meta.pages > 1 && <Pagination page={page} pages={meta.pages} onChange={setPage} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Métodos de pago
// ─────────────────────────────────────────────────────────────────────────────

function PaymentMethodsTab({ token }: { token: string | undefined }): JSX.Element {
  const [rows, setRows] = useState<PaymentMethodRow[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    apiFetch(`/payment-methods?page=${page}&pageSize=25`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json() as Promise<{ data: PaymentMethodRow[]; meta: ListMeta }>;
      })
      .then(({ data, meta: m }) => {
        setRows(data);
        setMeta(m);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Error cargando métodos de pago'),
      )
      .finally(() => setLoading(false));
  }, [token, page]);

  if (loading) return <LoadingSkeleton rows={5} />;
  if (error) return <p className="text-red-600 text-sm">{error}</p>;
  if (!rows.length)
    return (
      <EmptyState title="Sin métodos de pago" description="No hay métodos de pago registrados." />
    );

  return (
    <div>
      {meta && <p className="mb-4 text-sm text-gray-500">{meta.total} métodos de pago</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {[
                'Tenant',
                'Gateway',
                'Marca',
                'Últimos 4',
                'Vencimiento',
                'Por defecto',
                'Estado',
              ].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{row.tenant.name}</td>
                <td className="px-4 py-3 text-gray-600">{row.gateway}</td>
                <td className="px-4 py-3 text-gray-600">{row.brand ?? '—'}</td>
                <td className="px-4 py-3 text-gray-600">{row.last4 ? `••${row.last4}` : '—'}</td>
                <td className="px-4 py-3 text-gray-600">
                  {row.expMonth && row.expYear ? `${row.expMonth}/${row.expYear}` : '—'}
                </td>
                <td className="px-4 py-3">
                  {row.isDefault ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Sí
                    </span>
                  ) : (
                    <span className="text-gray-400">No</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={row.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta && meta.pages > 1 && <Pagination page={page} pages={meta.pages} onChange={setPage} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab: Eventos webhook
// ─────────────────────────────────────────────────────────────────────────────

function WebhookEventsTab({ token }: { token: string | undefined }): JSX.Element {
  const [rows, setRows] = useState<WebhookEventRow[]>([]);
  const [meta, setMeta] = useState<ListMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!token) return;
    setLoading(true);

    apiFetch(`/webhook-events?page=${page}&pageSize=50`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text());
        return r.json() as Promise<{ data: WebhookEventRow[]; meta: ListMeta }>;
      })
      .then(({ data, meta: m }) => {
        setRows(data);
        setMeta(m);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Error cargando eventos webhook'),
      )
      .finally(() => setLoading(false));
  }, [token, page]);

  if (loading) return <LoadingSkeleton rows={8} />;
  if (error) return <p className="text-red-600 text-sm">{error}</p>;
  if (!rows.length)
    return <EmptyState title="Sin eventos" description="No hay eventos de webhook registrados." />;

  return (
    <div>
      {meta && <p className="mb-4 text-sm text-gray-500">{meta.total} eventos</p>}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Fecha', 'Tipo de evento', 'ID externo', 'Payment ID', 'Actor'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={String(row.id)} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                  {new Date(row.createdAt).toLocaleString('es-DO')}
                </td>
                <td className="px-4 py-3 font-mono text-gray-700 text-xs">{row.eventType}</td>
                <td className="px-4 py-3 font-mono text-gray-500 text-xs">
                  {row.externalEventId
                    ? row.externalEventId.length > 32
                      ? `${row.externalEventId.slice(0, 32)}…`
                      : row.externalEventId
                    : '—'}
                </td>
                <td className="px-4 py-3 font-mono text-gray-500 text-xs">
                  {row.paymentId ? `${row.paymentId.slice(0, 8)}…` : '—'}
                </td>
                <td className="px-4 py-3 text-gray-500">{row.actorKind}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {meta && meta.pages > 1 && <Pagination page={page} pages={meta.pages} onChange={setPage} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componentes compartidos
// ─────────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }): JSX.Element {
  const map: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    succeeded: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    suspended: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-600',
    skipped: 'bg-gray-100 text-gray-600',
    expired: 'bg-gray-100 text-gray-600',
    revoked: 'bg-gray-100 text-gray-600',
  };
  const cls = map[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function Pagination({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (p: number) => void;
}): JSX.Element {
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
      >
        Anterior
      </button>
      <span>
        Página {page} de {pages}
      </span>
      <button
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
        className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
      >
        Siguiente
      </button>
    </div>
  );
}
